"use client";

import type { CountryCode } from "libphonenumber-js";
import { useEffect, useRef, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import ReactCountryFlag from "react-country-flag";

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@fluid-app/ui";

// Define the country option interface
export interface CountryOption {
  code: CountryCode;
  name: string;
  dialCode: string;
}

// Get country name from country code
const getCountryName = (countryCode: string) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode);
  } catch {
    return countryCode;
  }
};

// Get all countries supported by libphonenumber-js
const countries = getCountries();

// Format the country data for use in the combobox
const countryOptions: CountryOption[] = countries
  .map((country) => ({
    code: country,
    name: getCountryName(country) ?? country,
    dialCode: `+${getCountryCallingCode(country)}`,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export interface CountrySelectorProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (country: CountryOption) => void;
  containerRoot?: ShadowRoot | Element;
}

export function CountrySelector({
  value,
  onChange,
  onSelect,
  containerRoot,
}: CountrySelectorProps) {
  const [open, setOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const justClosedRef = useRef(false);

  // Initialize with a default country to avoid undefined values
  const getInitialCountry = (): CountryOption => {
    const found = countryOptions.find((option) => option.code === value);
    const usCountry = countryOptions.find((option) => option.code === "US");
    return (
      found ??
      usCountry ??
      countryOptions[0] ?? {
        code: "US",
        name: "United States",
        dialCode: "+1",
      }
    );
  };

  const [selectedCountry, setSelectedCountry] =
    useState<CountryOption>(getInitialCountry());

  // Update selected country when value changes
  useEffect(() => {
    const country = countryOptions.find((option) => option.code === value);
    if (country) {
      setSelectedCountry(country);
    }
  }, [value]);

  const handleSelect = (country: CountryOption) => {
    setSelectedCountry(country);
    onChange(country.code);
    setOpen(false);

    if (onSelect) {
      onSelect(country);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    // If trying to open immediately after closing, prevent it
    if (newOpen && justClosedRef.current) {
      justClosedRef.current = false;
      return;
    }

    // If closing, mark that we just closed
    if (!newOpen && open) {
      justClosedRef.current = true;
      // Clear the flag after a short delay
      setTimeout(() => {
        justClosedRef.current = false;
      }, 100);
    }

    setOpen(newOpen);

    // Focus the search input when opening
    if (newOpen) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        } else {
          // Fallback: search within the correct container
          const container = containerRoot ?? document;
          const searchInput = container.querySelector("[cmdk-input]");
          if (searchInput && searchInput instanceof HTMLInputElement) {
            searchInput.focus();
          }
        }
      }, 150);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className="flex h-auto w-full items-center justify-between border-0 bg-transparent p-2 pl-0 text-foreground transition-colors hover:bg-transparent hover:text-foreground focus:bg-transparent focus:text-foreground focus:ring-0"
        >
          <div className="flex items-center gap-1">
            <ReactCountryFlag
              countryCode={selectedCountry.code}
              svg
              style={{
                width: "1em",
                height: "1em",
              }}
              aria-label={selectedCountry.name}
            />
            <span className="whitespace-nowrap text-xs font-medium">
              {selectedCountry.dialCode}
            </span>
          </div>
          <ChevronDownIcon className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0" containerRoot={containerRoot}>
        <Command>
          <CommandInput
            placeholder="Search country..."
            className="rounded-none border-0 border-b border-border/50 focus:border-primary/30 focus:ring-0"
            autoFocus
            ref={searchInputRef}
          />
          <CommandEmpty className="py-6 text-sm text-muted-foreground">
            No country found.
          </CommandEmpty>
          <CommandList>
            <CommandGroup>
              {countryOptions.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.dialCode} ${country.code}`}
                  onSelect={() => handleSelect(country)}
                  className="hover:bg-primary/8 focus:bg-primary/8 flex cursor-pointer items-center gap-2 text-foreground transition-colors hover:text-foreground focus:text-foreground data-[selected=true]:bg-primary/10 data-[selected=true]:text-foreground"
                >
                  <ReactCountryFlag
                    countryCode={country.code}
                    svg
                    style={{
                      width: "1.2em",
                      height: "1.2em",
                    }}
                    aria-label={country.name}
                  />
                  <span className="font-medium">{country.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground hover:text-muted-foreground">
                    {country.dialCode}
                  </span>
                  {country.code === selectedCountry.code && (
                    <CheckIcon className="ml-1 h-4 w-4 text-primary" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
